import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Checkbox,
  Typography,
  Box,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { PhoneSearchResult } from '../types/accountSync';
import { formatPhoneNumber } from '../utils/phoneNumberUtils';
import { UI_TEXTS, DEFAULT_GROUP_SETTINGS } from '../config/accountSyncConfig';

interface AddPhoneDialogProps {
  open: boolean;
  onClose: () => void;
  currentPhone: string;
  onAdd: (phones: string[]) => Promise<void>;
  onSearch: (query: string) => Promise<PhoneSearchResult[]>;
}

const AddPhoneDialog: React.FC<AddPhoneDialogProps> = ({
  open,
  onClose,
  currentPhone,
  onAdd,
  onSearch,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PhoneSearchResult[]>([]);
  const [selectedPhones, setSelectedPhones] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 검색 실행
  const handleSearch = useCallback(async () => {
    if (searchQuery.length < DEFAULT_GROUP_SETTINGS.MIN_SEARCH_LENGTH) {
      setError(`최소 ${DEFAULT_GROUP_SETTINGS.MIN_SEARCH_LENGTH}자리 이상 입력해주세요.`);
      return;
    }

    setError(null);
    setSearching(true);
    setSearchResults([]);
    setSelectedPhones([]);

    try {
      const results = await onSearch(searchQuery);
      
      // 현재 전화번호 제외
      const filteredResults = results.filter(
        result => result.phonenumber !== currentPhone
      );
      
      setSearchResults(filteredResults);
      
      if (filteredResults.length === 0) {
        setError(UI_TEXTS.INFO_NO_RESULTS);
      }
    } catch (err) {
      setError('검색 중 오류가 발생했습니다.');
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, currentPhone, onSearch]);

  // 선택 토글
  const handleTogglePhone = (phone: string) => {
    setSelectedPhones(prev => {
      if (prev.includes(phone)) {
        return prev.filter(p => p !== phone);
      }
      
      // 최대 선택 개수 확인
      if (prev.length >= DEFAULT_GROUP_SETTINGS.MAX_GROUP_SIZE - 1) {
        setError(UI_TEXTS.INFO_MAX_GROUP_SIZE);
        return prev;
      }
      
      return [...prev, phone];
    });
    setError(null);
  };

  // 등록 처리
  const handleRegister = async () => {
    if (selectedPhones.length === 0) {
      setError('추가할 전화번호를 선택해주세요.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onAdd(selectedPhones);
      handleClose();
    } catch (err: any) {
      setError(err.message || '등록 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 다이얼로그 닫기
  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setSelectedPhones([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{UI_TEXTS.BUTTON_ADD_PHONE}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* 검색 입력 */}
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            label={UI_TEXTS.LABEL_PHONE_SEARCH}
            placeholder={UI_TEXTS.PLACEHOLDER_SEARCH}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            disabled={searching || loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleSearch} disabled={searching}>
                    {searching ? <CircularProgress size={20} /> : <SearchIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* 에러 메시지 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* 검색 결과 */}
        {searchResults.length > 0 && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              검색 결과 ({searchResults.length}개)
            </Typography>
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.phonenumber}
                  disablePadding
                  secondaryAction={
                    result.isInGroup && (
                      <Chip 
                        label="그룹 있음" 
                        size="small" 
                        color="warning" 
                      />
                    )
                  }
                >
                  <ListItemButton
                    onClick={() => handleTogglePhone(result.phonenumber)}
                    disabled={result.isInGroup || loading}
                  >
                    <Checkbox
                      edge="start"
                      checked={selectedPhones.includes(result.phonenumber)}
                      disabled={result.isInGroup || loading}
                    />
                    <ListItemText
                      primary={result.username}
                      secondary={
                        <Box>
                          <Typography variant="body2">
                            {formatPhoneNumber(result.phonenumber)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            만료일: {result.expiredate}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* 선택된 항목 표시 */}
        {selectedPhones.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              선택된 추가폰 ({selectedPhones.length}개)
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {UI_TEXTS.BUTTON_CANCEL}
        </Button>
        <Button
          onClick={handleRegister}
          variant="contained"
          disabled={selectedPhones.length === 0 || loading}
        >
          {loading ? <CircularProgress size={20} /> : UI_TEXTS.BUTTON_REGISTER}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPhoneDialog;